import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import ErrorMessage from "../Elements/ErrorMessage/ErrorMessage";
import SuccessMessage from "../Elements/SuccessMessage/SuccessMessage";

import WalletService from "../../services/WalletService";
import { fetchAvailableProviders } from "../../services/PaymentService";
import { validateTopUpInput, parseTopUpError } from "../../utils/topUpErrorHandler";
import { resolvePaymentProvider } from "../../utils/paymentMethods";

import applePayIcon from "../../assets/icons/payment-methods/apple-pay.svg";
import cardIcon from "../../assets/icons/payment-methods/credit-card.png";
import googlePayIcon from "../../assets/icons/payment-methods/g-pay.png";
import paypalIcon from "../../assets/icons/payment-methods/paypal.png";
import blikIcon from "../../assets/icons/payment-methods/blik-seeklogo.png";
import stripeIcon from "../../assets/icons/payment-methods/stripe.png";

import "./ProfileModals.css";
import { trackAnalyticsEvent } from "../../analytics/analytics";
import { appendCampaignParamsToUrl } from "../../analytics/campaignTracker";
import { AuthContext } from "../../context/AuthContext";

const PENDING_WALLET_TOPUP_PROVIDER_KEY = "pending_wallet_topup_provider";

const TopUpModal = ({ isOpen, onClose, walletId, currency, onToppedUp, geoCountryCode, geoReady }) => {
  const { userId } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || "en";

  // Top-up amount selection
  const [selectedAmount, setSelectedAmount] = useState(null);

  // Payment method selection
  const [paymentOption, setPaymentOption] = useState(null);

  // Dynamic providers state
  const [providers, setProviders] = useState([]);
  const [fetchingProviders, setFetchingProviders] = useState(false);

  // Voucher validation state
  const [voucher, setVoucher] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [isVoucherValid, setIsVoucherValid] = useState(null);
  const [topUpAmounts, setTopUpAmounts] = useState([]);

  // General top-up form state
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sending, setSending] = useState(false);

  const hasStripe = providers.some((p) => p.code?.toLowerCase() === "stripe");
  const hasPaypal = providers.some((p) => p.code?.toLowerCase() === "paypal");

  const isPolishLanguage = (i18n.language || "").toLowerCase().startsWith("pl");
  const showBlik = (isPolishLanguage || (geoReady && geoCountryCode === "PL")) && hasStripe;

  // Load available top-up amounts
  useEffect(() => {
    const loadAmounts = async () => {
      if (isOpen && currency) {
        const amounts = await WalletService.getTopUpAmounts(currency);
        setTopUpAmounts(amounts);
        setSelectedAmount(amounts[0] || 5);
      }
    };

    loadAmounts();
  }, [isOpen, currency]);

  // Fetch payment providers with graceful fallback
  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;

    const loadProviders = async () => {
      try {
        setFetchingProviders(true);
        const data = await fetchAvailableProviders();
        if (isMounted) {
          setProviders(data?.items || []);
        }
      } catch (err) {
        console.error("Error loading payment providers:", err);
        if (isMounted) {
          // Safe fallback matching PaymentModal behavior
          setProviders([{ code: "stripe" }, { code: "paypal" }]);
        }
      } finally {
        if (isMounted) setFetchingProviders(false);
      }
    };

    loadProviders();
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  // Synchronize default payment method with available providers
  useEffect(() => {
    if (!isOpen) return;

    if (hasStripe) {
      setPaymentOption("card");
    } else if (hasPaypal) {
      setPaymentOption("paypal");
    } else {
      setPaymentOption(null);
    }
  }, [isOpen, hasStripe, hasPaypal]);

  const resetVoucherState = () => {
    setVoucher("");
    setPromoDiscount(0);
    setIsVoucherValid(null);
  };

  const resetState = () => {
    setSelectedAmount(5);
    resetVoucherState();
    setError("");
    setSuccess("");
    setSending(false);
  };

  useEffect(() => {
    if (isOpen) resetState();
  }, [isOpen]);

  const handleAmountClick = (amount) => {
    setSelectedAmount(amount);
    if (promoDiscount > 0 && amount < promoDiscount) {
      setPromoDiscount(0);
      setIsVoucherValid(null);
    }
    setError("");
  };

  const handleSelectMethod = (method) => {
    if (paymentOption === method) return;
    setPaymentOption(method);
  };

  const handleCloseWithRefresh = async () => {
    if (success) {
      onToppedUp();
    }
    onClose();
  };

  const finalAmount = promoDiscount > 0 && isVoucherValid === true ? selectedAmount - promoDiscount : selectedAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!paymentOption || sending || fetchingProviders) return;

    setError("");
    setSuccess("");

    const validationError = validateTopUpInput({
      selectedAmount,
      voucher,
      promoDiscount,
      currency,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    const provider = resolvePaymentProvider(paymentOption);

    try {
      setSending(true);

      const payload = {
        request: {
          wallet_id: walletId,
          amount: finalAmount,
          currency,
          provider: provider,
          promocode: isVoucherValid === true ? voucher.trim() : null,
        },
        external: {
          frontend_url: appendCampaignParamsToUrl(`${window.location.origin}/${currentLang}/account`),
          product_name: "Wallet Top-Up",
        },
      };

      const response = await WalletService.topUp(payload);

      const stripeMatch = response.redirect_url?.match(/(cs_[a-zA-Z0-9_]+)/);
      const stripeSessionId = stripeMatch ? stripeMatch[1] : "";

      const pendingAnalyticsData = {
        transaction_id: String(response.transaction_id || response.id || `topup_${Date.now()}`),
        stripe_session_id: stripeSessionId,
        payment_id: String(response.payment_id || response.id || ""),
        user_id: String(userId || ""),
        value: Number(finalAmount),
        price: Number(finalAmount),
        currency: currency || "EUR",
        coupon: isVoucherValid === true ? voucher.trim() : "",
        payment_type: paymentOption,
        purchase_type: "wallet_topup",
        item_id: "wallet_balance",
        item_name: "Wallet Top-Up",
        item_category: "Wallet",
        quantity: 1,
      };

      // 1. External redirect flow
      if (response.success && response.redirect_url) {
        try {
          if (provider === "paypal") {
            localStorage.setItem(PENDING_WALLET_TOPUP_PROVIDER_KEY, provider);
          } else {
            localStorage.removeItem(PENDING_WALLET_TOPUP_PROVIDER_KEY);
          }
        } catch (storageError) {
          console.error("Failed to save wallet top-up payment provider:", storageError);
        }
        localStorage.setItem("pending_wallet_topup", JSON.stringify(pendingAnalyticsData));
        window.location.href = response.redirect_url;
        return;
      }

      // 2. Direct completion flow
      if (response.success) {
        resetVoucherState();
        setSuccess(
          `Top-up successful! +${response.topuped_amount?.toLocaleString() || finalAmount} ${response.currency || currency}`,
        );
        trackAnalyticsEvent.walletTopUp(pendingAnalyticsData);
        return;
      }
    } catch (err) {
      const friendlyError = parseTopUpError(err);
      setError(friendlyError);
    } finally {
      setSending(false);
    }
  };

  const isActionDisabled = !paymentOption || !selectedAmount || sending || fetchingProviders;

  return (
    <Modal
      isOpen={isOpen}
      onClose={sending ? () => {} : handleCloseWithRefresh}
      title={t("profile.topUpWallet")}
      actions={
        <>
          <SecondButton onClick={onClose} disabled={sending}>
            {t("common.cancel")}
          </SecondButton>
          <MainButton onClick={handleSubmit} disabled={isActionDisabled}>
            {sending ? t("cart.processing") : t("profile.topUp")}
          </MainButton>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="profile-modal-form">
        {/* Selection buttons */}
        <div className="profile-topup-amounts">
          {topUpAmounts.map((amount) => (
            <button
              type="button"
              key={amount}
              className={`profile-topup-amount-button ${selectedAmount === amount ? "selected" : ""}`}
              onClick={() => handleAmountClick(amount)}
              disabled={sending}
            >
              {amount} {currency}
            </button>
          ))}
        </div>

        {/* Payment Methods Section */}
        <label className="profile-topup-method-label">
          {t("cart.paymentMethod")}

          <div className={`payment-methods-group ${sending || fetchingProviders ? "disabled-area" : ""}`}>
            {hasStripe && (
              <div className="payment-methods-row payment-methods-row--two">
                <button
                  type="button"
                  className={`payment-method-tile payment-method-tile--logo-only ${
                    paymentOption === "apple_google_pay" ? "payment-method-tile--active" : ""
                  }`}
                  onClick={() => handleSelectMethod("apple_google_pay")}
                  disabled={sending}
                  aria-label="Apple Pay / Google Pay"
                  title="Apple Pay / Google Pay"
                >
                  <div className="payment-method-tile__logos-inline">
                    <img
                      src={applePayIcon}
                      alt="Apple Pay"
                      className="payment-method-tile__icon payment-method-tile__icon--applepay"
                    />
                    <span className="payment-method-tile__slash">/</span>
                    <img
                      src={googlePayIcon}
                      alt="Google Pay"
                      className="payment-method-tile__icon payment-method-tile__icon--gpay"
                    />
                  </div>
                </button>

                <button
                  type="button"
                  className={`payment-method-tile ${paymentOption === "card" ? "payment-method-tile--active" : ""}`}
                  onClick={() => handleSelectMethod("card")}
                  disabled={sending}
                  aria-label="Card"
                  title="Card"
                >
                  <img src={cardIcon} alt="Card" className="payment-method-tile__icon payment-method-tile__icon--card" />
                  <span className="payment-method-tile__label">Card</span>
                </button>
              </div>
            )}

            {(hasPaypal || showBlik) && (
              <div className={`payment-methods-row ${hasPaypal && showBlik ? "payment-methods-row--two" : "payment-methods-row--full"}`}>
                {hasPaypal && (
                  <button
                    type="button"
                    className={`payment-method-tile payment-method-tile--logo-only ${
                      paymentOption === "paypal" ? "payment-method-tile--active" : ""
                    }`}
                    onClick={() => handleSelectMethod("paypal")}
                    disabled={sending}
                    aria-label="PayPal"
                    title="PayPal"
                  >
                    <img
                      src={paypalIcon}
                      alt="PayPal"
                      className="payment-method-tile__icon payment-method-tile__icon--wide"
                    />
                  </button>
                )}

                {showBlik && (
                  <button
                    type="button"
                    className={`payment-method-tile payment-method-tile--logo-only ${
                      paymentOption === "blik" ? "payment-method-tile--active" : ""
                    }`}
                    onClick={() => handleSelectMethod("blik")}
                    disabled={sending}
                    aria-label="BLIK"
                    title="BLIK"
                  >
                    <img
                      src={blikIcon}
                      alt="BLIK"
                      className="payment-method-tile__icon payment-method-tile__icon--wide"
                    />
                  </button>
                )}
              </div>
            )}

            {!hasStripe && !hasPaypal && !fetchingProviders && (
              <p className="payment-panel__subtitle">
                {t("cart.noPaymentMethods", "No payment methods currently available.")}
              </p>
            )}
          </div>
        </label>

        {isVoucherValid === true && promoDiscount > 0 && (
          <div className="profile-promo-discount">
            You'll pay: {(selectedAmount - promoDiscount).toFixed(2)} {currency} (promo: -{promoDiscount.toFixed(2)}{" "}
            {currency})
          </div>
        )}

        {/* Top-up form error/success */}
        {error && (
          <div className="profile-topup-error-wrapper">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}

        {success && (
          <div className="profile-topup-success-wrapper">
            <SuccessMessage message={success} onClose={() => setSuccess("")} />
          </div>
        )}
      </form>
    </Modal>
  );
};

export default TopUpModal;
